import React from 'react';
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd';
import Box from '@mui/material/Box';
import { StrictModeDroppable } from './StrictModeDroppable';

export interface DraggableListProps<T> {
  items: T[];
  onDragEnd: (result: DropResult) => void;
  renderItem: (item: T, index: number) => React.ReactNode;
  getItemId: (item: T) => string;
  droppableId?: string;
  sx?: any;
}

export function DraggableList<T>({ 
  items, 
  onDragEnd, 
  renderItem, 
  getItemId,
  droppableId = 'droppable-list',
  sx = {}
}: DraggableListProps<T>) {
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <StrictModeDroppable droppableId={droppableId}>
        {(provided) => (
          <Box
            {...provided.droppableProps}
            ref={provided.innerRef}
            sx={{ 
              minHeight: '100px',
              position: 'relative',
              '& > *': {
                position: 'relative',
                zIndex: 1
              },
              ...sx
            }}
          >
            {items.map((item, index) => {
              const draggableId = getItemId(item);
              return (
                <Draggable
                  key={draggableId}
                  draggableId={draggableId}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      sx={{
                        ...provided.draggableProps.style,
                        transition: snapshot.isDragging 
                          ? provided.draggableProps.style?.transition 
                          : 'transform 0.2s cubic-bezier(0.2, 0, 0, 1)',
                        opacity: snapshot.isDragging ? 0.7 : 1,
                        cursor: 'grab',
                        '&:active': {
                          cursor: 'grabbing',
                        },
                        position: 'relative',
                        zIndex: snapshot.isDragging ? 2 : 1,
                        borderRadius: '15px',
                      }}
                    >
                      {renderItem(item, index)}
                    </Box>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </Box>
        )}
      </StrictModeDroppable>
    </DragDropContext>
  );
}
